import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { add, format, parseISO } from "date-fns";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Grid,
  Tooltip,
} from "@material-ui/core";
import {
  Edit,
  CheckCircle,
  SignalCellularConnectedNoInternet2Bar,
  SignalCellularConnectedNoInternet0Bar,
  SignalCellular4Bar,
  CropFree,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp,
} from "@material-ui/icons";

import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import formatSerializedId from "../../utils/formatSerializedId";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";
import { Can } from "../../components/Can";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.padding,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(14),
    border: "1px solid #dadde9",
    maxWidth: 450,
  },
  tooltipPopper: {
    textAlign: "center",
  },
  buttonProgress: {
    color: green[500],
  },
  cardRoot: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    border: "1px solid transparent",
    "&:hover": {
      transform: "scale(1.03)",
      borderColor: theme.palette.primary.main,
      boxShadow: theme.shadows[4],
    },
  },
  cardActions: {
    marginTop: "auto",
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    alignItems: "center",
  },
  editDeleteWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  statusWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginLeft: theme.spacing(1),
    whiteSpace: "nowrap",
  },
  cardHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.5),
  },
  cardHeaderName: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
  },
  actionButtonsWrapper: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  qrButtons: {
    minWidth: 120,
  },
}));

function CircularProgressWithLabel(props) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="caption" component="div" color="textSecondary">
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const CustomToolTip = ({ title, content, children }) => {
  const classes = useStyles();

  return (
    <Tooltip
      arrow
      classes={{
        tooltip: classes.tooltip,
        popper: classes.tooltipPopper,
      }}
      title={
        <React.Fragment>
          <Typography gutterBottom color="inherit">
            {title}
          </Typography>
          {content && <Typography>{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};

const IconChannel = (channel) => {
  switch (channel) {
    case "facebook":
      return <Facebook style={{ color: "#3b5998" }} />;
    case "instagram":
      return <Instagram style={{ color: "#e1306c" }} />;
    case "whatsapp":
      return <WhatsApp style={{ color: "#25d366" }} />;
    default:
      return "error";
  }
};

const statusLabels = {
  DISCONNECTED: "Desconectado",
  OPENING: "Conectando",
  qrcode: "QR Code",
  CONNECTED: "Conectado",
  TIMEOUT: "Timeout",
  PAIRING: "Emparelhando",
};

const Connections = () => {
  const classes = useStyles();

  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [statusImport, setStatusImport] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const history = useHistory();
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);

  const { user, socket } = useContext(AuthContext);

  const companyId = user.companyId;

  const { getPlanCompany } = usePlans();

  const [planConfig, setPlanConfig] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      setPlanConfig(planConfigs);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then(() => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;

      api
        .post("/facebook", {
          addInstagram: true,
          facebookUserId: id,
          facebookUserToken: accessToken,
        })
        .then(() => {
          toast.success(i18n.t("connections.facebook.success"));
        })
        .catch((error) => {
          toastError(error);
        });
    }
  };

  useEffect(() => {
    socket.on(`importMessages-${user.companyId}`, (data) => {
      if (data.action === "refresh") {
        setStatusImport([]);
        history.go(0);
      }
      if (data.action === "update") {
        setStatusImport(data.status);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsApps]);

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = () => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const openInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    if (action === "closedImported") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.closedImportedTitle"),
        message: i18n.t("connections.confirmationModal.closedImportedMessage"),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "closedImported") {
      try {
        await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.closedimported"));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
  };

  const renderImportButton = (whatsApp) => {
    if (whatsApp?.statusImportMessages === "renderButtonCloseTickets") {
      return (
        <Button
          style={{ marginLeft: 12 }}
          size="small"
          variant="outlined"
          color="primary"
          className={classes.qrButtons}
          onClick={() => {
            handleOpenConfirmationModal("closedImported", whatsApp.id);
          }}
        >
          {i18n.t("connections.buttons.closedImported")}
        </Button>
      );
    }

    if (whatsApp?.importOldMessages) {
      let isTimeStamp = !isNaN(
        new Date(Math.floor(whatsApp?.statusImportMessages)).getTime()
      );

      if (isTimeStamp) {
        const ultimoStatus = new Date(
          Math.floor(whatsApp?.statusImportMessages)
        ).getTime();
        const dataLimite = +add(ultimoStatus, { seconds: +35 }).getTime();
        if (dataLimite > new Date().getTime()) {
          return (
            <>
              <Button
                disabled
                style={{ marginLeft: 12 }}
                size="small"
                endIcon={
                  <CircularProgress size={12} className={classes.buttonProgress} />
                }
                variant="outlined"
                color="primary"
                className={classes.qrButtons}
              >
                {i18n.t("connections.buttons.preparing")}
              </Button>
            </>
          );
        }
      }
    }
  };

  const renderActionButtons = (whatsApp) => {
    return (
      <Box className={classes.actionButtonsWrapper}>
        {(whatsApp.status === "qrcode" ||
          (whatsApp.transport === "evolution" && whatsApp.qrcode)) && (
          <Can
            role={
              user.profile === "user" && user.allowConnections === "enabled"
                ? "admin"
                : user.profile
            }
            perform="connections-page:addConnection"
            yes={() => (
              <Button
                size="small"
                variant="contained"
                color="primary"
                className={classes.qrButtons}
                onClick={() => handleOpenQrModal(whatsApp)}
              >
                {i18n.t("connections.buttons.qrcode")}
              </Button>
            )}
          />
        )}
        {whatsApp.status === "DISCONNECTED" && (
          <Can
            role={
              user.profile === "user" && user.allowConnections === "enabled"
                ? "admin"
                : user.profile
            }
            perform="connections-page:addConnection"
            yes={() => (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  className={classes.qrButtons}
                  onClick={() => handleStartWhatsAppSession(whatsApp.id)}
                >
                  {i18n.t("connections.buttons.tryAgain")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  className={classes.qrButtons}
                  onClick={() => handleRequestNewQrCode(whatsApp.id)}
                >
                  {i18n.t("connections.buttons.newQr")}
                </Button>
              </>
            )}
          />
        )}
        {(whatsApp.status === "CONNECTED" ||
          whatsApp.status === "PAIRING" ||
          whatsApp.status === "TIMEOUT") && (
          <Can
            role={user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    handleOpenConfirmationModal("disconnect", whatsApp.id);
                  }}
                  className={classes.qrButtons}
                >
                  {i18n.t("connections.buttons.disconnect")}
                </Button>

                {renderImportButton(whatsApp)}
              </>
            )}
          />
        )}
        {whatsApp.status === "OPENING" && (
          <Button size="small" variant="outlined" disabled color="default" className={classes.qrButtons}>
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
      </Box>
    );
  };

  const renderStatusToolTips = (whatsApp) => {
    const statusText = statusLabels[whatsApp.status] || whatsApp.status;

    return (
      <Box className={classes.statusWrapper}>
        {whatsApp.status === "DISCONNECTED" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.disconnected.title")}
            content={i18n.t("connections.toolTips.disconnected.content")}
          >
            <SignalCellularConnectedNoInternet0Bar color="secondary" />
          </CustomToolTip>
        )}
        {whatsApp.status === "OPENING" && (
          <CircularProgress size={24} className={classes.buttonProgress} />
        )}
        {whatsApp.status === "qrcode" && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.qrcode.title")}
            content={i18n.t("connections.toolTips.qrcode.content")}
          >
            <CropFree />
          </CustomToolTip>
        )}
        {whatsApp.status === "CONNECTED" && (
          <CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
            <SignalCellular4Bar style={{ color: green[500] }} />
          </CustomToolTip>
        )}
        {(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
          <CustomToolTip
            title={i18n.t("connections.toolTips.timeout.title")}
            content={i18n.t("connections.toolTips.timeout.content")}
          >
            <SignalCellularConnectedNoInternet2Bar color="secondary" />
          </CustomToolTip>
        )}
        {/* Texto do status ao lado do ícone */}
        <Typography variant="body2" style={{ marginLeft: 6, fontWeight: 600 }}>
          {statusText}
        </Typography>
      </Box>
    );
  };

  const restartWhatsapps = async () => {
    try {
      await api.post(`/whatsapp-restart/`);
      toast.success(i18n.t("connections.waitConnection"));
    } catch (err) {
      toastError(err);
    }
  };

return (
  <MainContainer>
    <ConfirmationModal
      title={confirmModalInfo.title}
      open={confirmModalOpen}
      onClose={setConfirmModalOpen}
      onConfirm={handleSubmitConfirmationModal}
    >
      {confirmModalInfo.message}
    </ConfirmationModal>
    {qrModalOpen && (
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
    )}
    <WhatsAppModal
      open={whatsAppModalOpen}
      onClose={handleCloseWhatsAppModal}
      whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
    />
    {user.profile === "user" && user.allowConnections === "disabled" ? (
      <ForbiddenPage />
    ) : (
      <>
        <MainHeader>
          <Title>{i18n.t("connections.title")} ({whatsApps.length})</Title>
          <MainHeaderButtonsWrapper>
            <Button
              variant="contained"
              color="primary"
              onClick={restartWhatsapps}
            >
              {i18n.t("connections.restartConnections")}
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                openInNewTab(`https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`)
              }
            >
              {i18n.t("connections.callSupport")}
            </Button>
            <PopupState variant="popover" popupId="demo-popup-menu">
              {(popupState) => (
                <React.Fragment>
                  <Can
                    role={user.profile}
                    perform="connections-page:addConnection"
                    yes={() => (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          {...bindTrigger(popupState)}
                        >
                          {i18n.t("connections.newConnection")}
                        </Button>
                        <Menu {...bindMenu(popupState)}>
                          {/* WHATSAPP */}
                          <MenuItem
                            disabled={planConfig?.plan?.useWhatsapp ? false : true}
                            onClick={() => {
                              handleOpenWhatsAppModal();
                              popupState.close();
                            }}
                          >
                            <WhatsApp
                              fontSize="small"
                              style={{
                                marginRight: "10px",
                                color: "#25D366",
                              }}
                            />
                            WhatsApp
                          </MenuItem>
                          {/* FACEBOOK */}
                          <FacebookLogin
                            appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                            autoLoad={false}
                            fields="name,email,picture"
                            version="9.0"
                            scope={process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() === "TRUE"
                              ? "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                              : "public_profile,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"}
                            callback={responseFacebook}
                            render={(renderProps) => (
                              <MenuItem
                                disabled={planConfig?.plan?.useFacebook ? false : true}
                                onClick={renderProps.onClick}
                              >
                                <Facebook
                                  fontSize="small"
                                  style={{
                                    marginRight: "10px",
                                    color: "#3b5998",
                                  }}
                                />
                                Facebook
                              </MenuItem>
                            )}
                          />
                          {/* INSTAGRAM */}
                          <FacebookLogin
                            appId={process.env.REACT_APP_FACEBOOK_APP_ID}
                            autoLoad={false}
                            fields="name,email,picture"
                            version="9.0"
                            scope={process.env.REACT_APP_REQUIRE_BUSINESS_MANAGEMENT?.toUpperCase() === "TRUE"
                              ? "public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement,business_management"
                              : "public_profile,instagram_basic,instagram_manage_messages,pages_messaging,pages_show_list,pages_manage_metadata,pages_read_engagement"}
                            callback={responseInstagram}
                            render={(renderProps) => (
                              <MenuItem
                                disabled={planConfig?.plan?.useInstagram ? false : true}
                                onClick={renderProps.onClick}
                              >
                                <Instagram
                                  fontSize="small"
                                  style={{
                                    marginRight: "10px",
                                    color: "#e1306c",
                                  }}
                                />
                                Instagram
                              </MenuItem>
                            )}
                          />
                        </Menu>
                      </>
                    )}
                  />
                </React.Fragment>
              )}
            </PopupState>
          </MainHeaderButtonsWrapper>
        </MainHeader>
        <main className={classes.mainPaper}>
          {loading ? (
            <TableRowSkeleton />
          ) : (
            <Grid container spacing={3} style={{ padding: 16 }}>
              {whatsApps?.length > 0 &&
                whatsApps.map((whatsApp) => (
                  <Grid item xs={12} sm={6} md={4} key={whatsApp.id}>
                    <Card variant="outlined" className={classes.cardRoot}>
                      <CardContent style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                        <Box className={classes.cardHeaderRow}>
                          <Box className={classes.cardHeaderName}>
                            {IconChannel(whatsApp.channel)}
                            <Typography variant="h6" noWrap style={{ marginLeft: 8 }}>
                              {whatsApp.name}
                            </Typography>
                          </Box>
                          {renderStatusToolTips(whatsApp)}
                        </Box>

                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          {whatsApp.number && whatsApp.channel === "whatsapp"
                            ? formatSerializedId(whatsApp.number)
                            : whatsApp.number}
                        </Typography>

                        <Box mb={1}>
                          <Typography variant="caption" color="textSecondary">
                            {i18n.t("connections.table.lastUpdate")}:{" "}
                            {format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
                          </Typography>
                        </Box>

                        <Box display="flex" alignItems="center" mb={1}>
                          {whatsApp.isDefault && (
                            <>
                              <CheckCircle style={{ color: green[500], marginRight: 4 }} />
                              <Typography variant="body2">{i18n.t("connections.table.default")}</Typography>
                            </>
                          )}
                        </Box>

                        <Box className={classes.cardActions}>
                          {renderActionButtons(whatsApp)}

                          <Box className={classes.editDeleteWrapper}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditWhatsApp(whatsApp)}
                            >
                              <Edit />
                            </IconButton>

                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenConfirmationModal("delete", whatsApp.id)
                              }
                            >
                              <DeleteOutline />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
            </Grid>
          )}
        </main>
      </>
    )}
  </MainContainer>
);
};


export default Connections;
